import React from 'react';

const STEPS = [
  { key: 'read', label: 'Read theme', stages: [1, 2] },
  { key: 'tokens', label: 'Generate tokens', stages: [3] },
  { key: 'build', label: 'Build', stages: [4] },
  { key: 'deploy', label: 'PR + deploy', stages: [5, 6] },
];

// Collapse raw stage events (1-6) into the 4 user-facing steps.
export function deriveSteps(status, stages, errorMessage) {
  const byStage = {};
  (stages || []).forEach(e => { byStage[e.stage] = e; });
  const seen = Object.keys(byStage).map(Number);
  const maxSeen = seen.length ? Math.max(...seen) : 0;
  const hasFailedEvent = (stages || []).some(e => e.status === 'failed');

  return STEPS.map((step) => {
    const events = step.stages.map(s => byStage[s]).filter(Boolean);
    const failed = events.find(e => e.status === 'failed');
    const last = step.stages[step.stages.length - 1];
    const groupMax = Math.max(...step.stages);
    const groupMin = Math.min(...step.stages);

    if (failed) return { ...step, state: 'failed', detail: failed.message };
    if (status === 'success') return { ...step, state: 'done' };

    const laterStarted = maxSeen > groupMax;
    const lastComplete = byStage[last]?.status === 'complete';
    const touched = events.length > 0;
    const isCurrent = touched && !laterStarted && !lastComplete;
    const isFirstUntouched = maxSeen === 0 && groupMin === 1;

    if (status === 'failed' && !hasFailedEvent && (isCurrent || isFirstUntouched)) {
      return { ...step, state: 'failed', detail: errorMessage || 'Sync failed' };
    }
    if (laterStarted || lastComplete) return { ...step, state: 'done' };
    if (status === 'syncing' && (touched || isFirstUntouched)) return { ...step, state: 'active' };
    return { ...step, state: 'idle' };
  });
}

const Icon = ({ state, index }) => {
  if (state === 'done') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>;
  if (state === 'failed') return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
  if (state === 'active') return <span className="sync-step-spinner" />;
  return <span>{index + 1}</span>;
};

export const SyncStatusSteps = ({ status, stages, errorMessage, finishedAt, prNumber, githubRepo }) => {
  const steps = deriveSteps(status, stages, errorMessage);
  const prUrl = prNumber && githubRepo ? `https://github.com/${githubRepo}/pull/${prNumber}` : null;
  const finished = finishedAt ? new Date(finishedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;

  return (
    <div className={`sync-steps sync-steps-${status}`} data-testid="sync-status-steps" data-status={status}>
      <div className="sync-steps-track">
        {steps.map((s, i) => (
          <div key={s.key} className={`sync-step sync-step-${s.state}`} data-testid={`sync-step-${s.key}`} data-state={s.state}>
            <div className="sync-step-dot"><Icon state={s.state} index={i} /></div>
            <div className="sync-step-label">{s.label}</div>
            {s.state === 'failed' && s.detail && (
              <div className="sync-step-error" data-testid={`sync-step-${s.key}-error`} title={s.detail}>{s.detail}</div>
            )}
          </div>
        ))}
      </div>
      {status === 'success' && (
        <div className="sync-steps-footer" data-testid="sync-steps-footer">
          {finished && <span>Finished {finished}</span>}
          {prUrl && <a href={prUrl} target="_blank" rel="noopener noreferrer" data-testid="sync-steps-pr-link">PR #{prNumber} ↗</a>}
        </div>
      )}
    </div>
  );
};
